import { Component } from '@angular/core';
import { IonicPage, NavController, NavParams, LoadingController, ToastController, AlertController, Platform } from 'ionic-angular';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs/Rx';
import { CustomersService } from '../../services/customers/customers-service';
import { SubscriptionsService } from '../../services/subscriptions/subscriptions-service';
import { SubscriptionItem } from '../../models/subscription-item';
import { Customer } from '../../models/customer';

/**
 * Generated class for the CustomerBillingPage page.
 *
 * See https://ionicframework.com/docs/components/#navigation for more info on
 * Ionic pages and navigation.
 */

@IonicPage()
@Component({
  selector: 'page-customer-billing',
  templateUrl: 'customer-billing.html',
})
export class CustomerBillingPage {
  public Customer: Customer;
  private billingForm: FormGroup;
  public subscriptionItems: Array<any>;
  public expMonths: Array<number>;
  public expYears: Array<number>;
  public maxCcExp: string;
  public creditCardTypes: Array<any>;
  public isCordova: boolean;
  public selectOptions: any = { cssClass: null };

  ionViewCanEnter() {

    var yr: number = (new Date()).getFullYear();
    this.maxCcExp = `${yr+10}-12-31`;

    // available credit card types
    this.setCreditCardTypes();

    this.setCreditCardExpYears();

    return new Promise((resolve, reject) => {
      // 2E3THKQ3BVSAY
      //
      let itemId: number = this.Customer.get("meta").plan_id;
      let self: CustomerBillingPage = this;

      this.fetchSubscriptionItems()
        .subscribe(
          res => {
            // if the customer's plan is not in the response array,
            // fetch the individual plan id & append the list.
            //
            for(let s in self.subscriptionItems)
            {
              if(self.subscriptionItems[s].get("id") == itemId)
              {
                resolve(null);
                return;
              }
            }

            self.fetchSubscriptionItem(itemId)
              .subscribe(
                res => { resolve(null); },
                rej => { resolve(null); }
              )
          },
          rej => {
            this.toastCtrl.create({message: rej, duration: 6000, position: "top"}).present();
            resolve(null);
          }
        )
    })
  }

  ionViewDidLoad() {

    // Define custom select classes if
    // not in mobile env.
    this.selectOptions.cssClass = this.isCordova ? null : 'extra-wide';
  }

  private setCreditCardTypes() {
    this.creditCardTypes = [
      {
        type: "visa",
        label: "Visa"
      },
      {
        type: "mc",
        label: "Mastercard"
      },
      {
        type: "amex",
        label: "American Express"
      },
      {
        type: "discover",
        label: "Discover"
      },
      {
        type: "diners_club",
        label: "Diners Club"
      },
      {
        type: "jcb",
        label: "JCB"
      }
    ];
  }

  private setCreditCardExpYears() {
    this.expYears = [];
    let minYear = (new Date()).getFullYear() - 10;
    let maxYear = (new Date()).getFullYear() + 10;
    for(let i = maxYear; i >= minYear; i--)
    {
      this.expYears.push(i);
    }
  }

  // Load the available Subscription plans.
  fetchSubscriptionItems() {
    return Observable.create(observable => {
      this.subscriptionService.getItems().subscribe(items => {
        this.subscriptionItems = items
        .map(function(i){ return new SubscriptionItem(i); })
        .filter(function(i){ return !i.get("hidden"); });
        observable.next();
      },
      () => {
        observable.error("Failed to load the subscription plan list.");
      });
    });
  }

  fetchSubscriptionItem(itemId) {
    return Observable.create(observable => {
      this.subscriptionService.getItem(itemId).subscribe(item => {
        if(this.subscriptionItems)
        {
          this.subscriptionItems.push(new SubscriptionItem(item));
        }
        observable.next();
      },
      () => {
        observable.error("Failed to load the subscribed plan.");
      });
    });
  }

  closeAccount() {

    // ----------------------------------------------------------------
    // Make the user confirm twice.

    let alert = this.alertCtrl.create({
        title: 'Are You Sure?',
        message: 'You can not undo the following action.  Are you sure you want to delete '
              + [this.Customer.get("firstName"),this.Customer.get("lastName")+"'s"].join(" ") + ' account?',
        buttons: [
          {
            text: 'No. Go Back.',
            role: 'cancel',
            handler: () => {}
          },
          {
            text: 'Yes, Please close account',
            handler: () => {

              // ----------------------------------------------------------------
              // Proceed to delete account.

              let status : string = "canceled";

              this.Customer.get('meta').status = status;

              let self: CustomerBillingPage = this;

              let loading = this.loadingCtrl.create({
                content: "Saving..."
              });

              loading.present();

              this.custService.update(this.Customer.get("meta").subscription_id,{status: {status: status}})
                .subscribe(
                  res => {
                    loading.dismiss();

                    // toast!
                    let toast = this.toastCtrl.create({
                      message: `Success. ${self.Customer.name()}'s account was successfully closed.`,
                      duration: 6000,
                      position: "top"
                    });

                    toast.present();
                  },
                  rej => {
                    loading.dismiss();

                    let toast = this.toastCtrl.create({
                      message: rej.error.message,
                      duration: 8000,
                      position: "top"
                    });

                    toast.present();
                  }
                );
            }
          }
        ]
      });
      alert.present();
  }

  updBillingCCChange() {
    let required: boolean = !!this.billingForm.controls.updateBillingCC.value;
    let g: string;
    let a: Array<any>;
    let group: Object = { updateBillingCC: [], planId: [], ccType: [], ccNumber: [], ccExp: [], ccCvc: [], ccZipCode: [] }

    for(g in group)
    {
      a = [this.billingForm.controls[g].value];
      switch(g) {
        case "updateBillingCC":
          group[g] = a;
          break;

        case "planId":
          a.push(Validators.compose([Validators.required]));
          group[g] = a;
          break;

        case "ccType":
        case "ccExp":
          if(required)
          {
            a.push(Validators.compose([Validators.required]));
          }
          group[g] = a;
          break;

        case "ccNumber":
          if(required)
          {
            a.push(Validators.compose([Validators.required, Validators.minLength(14), Validators.maxLength(20)]));
          }
          group[g] = a;
          break;

        case "ccCvc":
          if(required)
          {
            a.push(Validators.compose([Validators.required, Validators.pattern('^[0-9]{3,5}$')]));
          }
          group[g] = a;
          break;

        case "ccZipCode":
          if(required)
          {
            a.push(Validators.compose([Validators.required, Validators.pattern('^[1-9]{1}[0-9]{4}$')]));
          }
          group[g] = a;
          break;

      }
    }

    this.billingForm = this.formBuilder.group(group);
  }

  // Key-press event handler sanitizes
  // input values for credit card numbers.
  numOnlyMask(keyCode) {
    return [8,48,49,50,51,52,53,54,55,56,57,189].indexOf(parseInt(keyCode)) > -1;
  }

  toCamelCase(str): string {
    return str.split("_").map(function(word,index){

      // If it is the first word make sure to lowercase all the chars.
      if(index == 0) {
        return String(word).toLowerCase();
      }

      // If it is not the first word only upper case the first char and lowercase the rest.
      return String(word).charAt(0).toUpperCase() + String(word).slice(1).toLowerCase();

    }).join("");
  }

  save(billingForm)
  {
    let self: CustomerBillingPage = this;
    let p: string;
    let updCC: boolean = !!this.billingForm.controls.updateBillingCC;

    for(p in billingForm)
    {
      if(billingForm[p] === this.Customer.get(p))
      {
        delete billingForm[p];
      }
      else if(updCC && String(p).indexOf("CC") > -1)
      {
        delete billingForm[p];
      }
    }

    if(Object.keys(billingForm).length === 0)
    {
      let toast = this.toastCtrl.create({
        message: `There were no changes to ${self.Customer.name()}'s account to save.`,
        duration: 6000,
        position: "top"
      });
      toast.present();
      return;
    }

    let loading = this.loadingCtrl.create({
      content: "Saving..."
    });

    loading.present();

    this.custService.update(this.Customer.get("meta").subscription_id,{billing: billingForm})
      .subscribe(
        res => {
          loading.dismiss();

          // change to camelCase
          let o: object = {};
          for(var e in res){ o[self.toCamelCase(e)] = res[e]; }

          // store customer record.
          self.Customer = new Customer(o);
          self.custService.set(self.Customer);

          // toast!
          let toast = this.toastCtrl.create({
            message: `Success. ${self.Customer.name()}'s account was successfully updated.`,
            duration: 6000,
            position: "top"
          });

          toast.present();
        },
        rej => {
          loading.dismiss();

          let toast = this.toastCtrl.create({
            message: rej.error.message,
            duration: 8000,
            position: "top"
          });

          toast.present();
        }
      );
  }

  constructor(public navCtrl: NavController,
    public navParams: NavParams,
    public custService: CustomersService,
    private toastCtrl: ToastController,
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
    private subscriptionService: SubscriptionsService,
    private platform: Platform,
    private formBuilder: FormBuilder) {

      // Identify if this is browser or device.
      this.isCordova = this.platform.is('cordova');

      this.Customer = this.custService.get();

      this.billingForm = this.formBuilder.group({
        updateBillingCC: [false],
        planId: [this.Customer.get("meta").plan_id, Validators.compose([Validators.required])],
        ccType: [''],
        ccNumber: [''],
        ccExp: [''],
        ccCvc: [''],
        ccZipCode: ['']
      });
  }
}



// WEBPACK FOOTER //
// ./src/pages/customer-billing/customer-billing.ts